import { format } from 'date-fns';

import { DatePicker } from '@components/datepicker';
import { EmailField } from '@components/email-field';
import {
  FieldMappingFileField,
  FieldMappingFileListField,
} from '@components/field-mapping-file-field';
import { FieldMappingNumberField } from '@components/field-mapping-number-field';
import { FieldMappingPhoneField } from '@components/field-mapping-phone-field';
import { Select } from '@components/select';
import { SelectMulti, SelectOption } from '@components/selectmulti';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { FieldMappingTextEditor } from '@components/text-editors';
import {
  ExtendedFieldMappingEditorValue,
  FieldMappingEditorValue,
  FieldMappingGroup,
} from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';

import { toMappedEditorValue } from './rehydrate-variables';
import { toSelectedOptions } from './serialize';
import { FieldConfigValue, JourneyAttributeType } from './types';
import {
  excludeVariableGroupTypes,
  FILE_VARIABLE_TYPES,
  filterVariableGroupsByTypes,
} from './variable-groups';

import { useList, useUsers } from '../hooks';

type ValueInputProps = {
  shortId: string;
  title: string;
  attributeType: JourneyAttributeType;
  options: string | null;
  value: FieldConfigValue | undefined;
  variableGroups: FieldMappingGroup[];
  fileVariableGroups: FieldMappingGroup[];
  fileMultiVariableGroups: FieldMappingGroup[];
  fieldMappingId: string;
  onChange: (shortId: string, value: FieldConfigValue) => void;
};

const VARIABLE_MAPPABLE_TYPES: JourneyAttributeType[] = ['text', 'paragraph'];

/**
 * Renders the appropriate value input for a single journey attribute.
 *
 * Text/paragraph reuse FieldMappingTextEditor so the serialized form is always a
 * correct FieldMappingEditorValue dict — the invariant required by
 * convert_fieldmapped_value on the backend (the same component + encoding the
 * add-to-journey sidebar uses).
 *
 * email/phone_number use core's EmailField / FieldMappingPhoneField, which are
 * the field-mapping-aware members of that same family: both wrap
 * NodeStyledFieldMappingTextEditor with a typed node extension and
 * `fieldType`, so they serialize to the very same shape while adding the typed
 * semantics a generic text editor cannot — an email pill that turns red when
 * validateEmail fails, and a phone entry popover backed by core's country-code
 * PhoneField. The plain @components/phone-field is deliberately NOT used here:
 * it parses its value through cleanPhoneNumber, which silently strips a
 * `{{ token }}` to an empty string and would destroy any field mapping.
 *
 * file/file_multi reuse FieldMappingFileField / FieldMappingFileListField
 * (mapping-only, uploads disabled) so file values are always
 * FieldMappingEditorValue dicts pointing at a mappable file source.
 *
 * Non-mappable types take literal values only: date (date picker, yyyy-MM-dd),
 * number/decimal (numeric input), boolean (switch), single_select/multi_select
 * (selects fed by the attribute's CompanyList via useList).
 */
export const ValueInput = ({
  shortId,
  title,
  attributeType,
  options,
  value,
  variableGroups,
  fileVariableGroups,
  fileMultiVariableGroups,
  fieldMappingId,
  onChange,
}: ValueInputProps) => {
  const { t } = useScopedTranslate(locale);
  const isSelectType = attributeType === 'single_select' || attributeType === 'multi_select';
  const listQuery = useList(isSelectType ? (options ?? '') : '');
  const usersQuery = useUsers();

  const currentStringValue = typeof value === 'string' ? value : undefined;
  // toMappedEditorValue, not serialize's toEditorValue: the latter wraps a
  // stored string as {value, variables: {}}, which renders a {{ token }} as
  // literal text and re-persists it mapless on the next save.
  const currentEditorValue = toMappedEditorValue(value, variableGroups);

  const handleEditorChange = (nextValue: FieldMappingEditorValue) => {
    onChange(shortId, nextValue);
  };

  /**
   * EmailField and FieldMappingPhoneField are single-value wrappers over their
   * list counterparts and emit `value[0]`, which is undefined once the last
   * pill is deleted. Store the empty-value marker instead, matching what every
   * other cleared editor here stores: `serialize.ts` normalises `value ?? ''`
   * on both sides of the per-job override diff, so `undefined` and `''` compare
   * identically — but only `''` survives a round-trip through the stored blob
   * as an explicit "configured, then emptied".
   */
  const handleTypedFieldChange = (nextValue: ExtendedFieldMappingEditorValue | undefined) => {
    onChange(shortId, nextValue ?? '');
  };

  if (attributeType === 'email') {
    return (
      <Stack space={1}>
        <EmailField
          label={title}
          hideLabel
          variableGroups={filterVariableGroupsByTypes(variableGroups, ['email'])}
          fieldMappingId={fieldMappingId}
          defaultValue={currentEditorValue}
          onChange={handleTypedFieldChange}
        />
      </Stack>
    );
  }

  if (attributeType === 'phone_number') {
    return (
      <Stack space={1}>
        <FieldMappingPhoneField
          label={title}
          hideLabel
          variableGroups={filterVariableGroupsByTypes(variableGroups, ['phone'])}
          fieldMappingId={fieldMappingId}
          defaultValue={currentEditorValue}
          onChange={handleTypedFieldChange}
          fieldMapText={{
            title: t('fieldMapTitle'),
            description: t('fieldMapDescription'),
          }}
        />
      </Stack>
    );
  }

  if (VARIABLE_MAPPABLE_TYPES.includes(attributeType)) {
    return (
      <Stack space={1}>
        <FieldMappingTextEditor
          label={title}
          hideLabel
          singleLine={attributeType !== 'paragraph'}
          plaintext
          // A file variable resolves to a string here, not a file: CV and Cover
          // letter are typed `file` and belong on a file field, so the picker does
          // not offer them.
          variableGroups={excludeVariableGroupTypes(variableGroups, FILE_VARIABLE_TYPES)}
          fieldMappingId={fieldMappingId}
          defaultValue={currentEditorValue}
          onChange={handleEditorChange}
          placeholder={t('enterValue')}
        />
      </Stack>
    );
  }

  if (attributeType === 'date') {
    return (
      <DatePicker
        label={title}
        hideLabel
        value={currentStringValue}
        onChange={(date) => {
          if (!date) {
            onChange(shortId, '');
            return;
          }
          onChange(shortId, format(date, 'yyyy-MM-dd'));
        }}
        placeholder={t('dateFormat')}
      />
    );
  }

  if (attributeType === 'number' || attributeType === 'decimal') {
    const isDecimal = attributeType === 'decimal';
    return (
      <Stack space={1}>
        <FieldMappingNumberField
          label={title}
          hideLabel
          isDecimal={isDecimal}
          // A decimal target accepts a whole number too; an integer target must
          // not be offered a decimal-typed variable.
          variableGroups={filterVariableGroupsByTypes(
            variableGroups,
            isDecimal ? ['number', 'decimal'] : ['number'],
          )}
          fieldMappingId={fieldMappingId}
          defaultValue={currentEditorValue}
          onChange={handleTypedFieldChange}
        />
      </Stack>
    );
  }

  if (attributeType === 'boolean') {
    return (
      <Stack orientation="horizontal" justify="flex-start" align="center">
        <Switch
          label={title}
          labelVisible={false}
          value={typeof value === 'boolean' ? value : false}
          onChange={(isOn) => onChange(shortId, isOn)}
        />
      </Stack>
    );
  }

  if (attributeType === 'single_select') {
    return (
      <Select
        label={title}
        hideLabel
        value={currentStringValue ?? null}
        options={(listQuery.data?.options ?? []).map((option) => ({
          value: option.shortId,
          label: option.label,
        }))}
        onChange={(nextValue) => onChange(shortId, nextValue)}
        placeholder={t('selectValue')}
      />
    );
  }

  if (attributeType === 'multi_select') {
    const listOptions: SelectOption[] = (listQuery.data?.options ?? []).map((option) => ({
      value: option.shortId,
      label: option.label,
    }));
    const selectedOptions = toSelectedOptions({
      value,
      options: listOptions,
      unavailableLabel: t('unavailableOption'),
    });
    return (
      <SelectMulti
        label={title}
        hideLabel
        value={selectedOptions}
        options={listOptions}
        onChange={(nextOptions) =>
          onChange(
            shortId,
            nextOptions.map((option) => option.value),
          )
        }
        placeholder={t('selectValue')}
      />
    );
  }

  if (attributeType === 'file') {
    return (
      <Stack space={1}>
        <FieldMappingFileField
          label={title}
          hideLabel
          allowUploadFiles={false}
          variableGroups={fileVariableGroups}
          fieldMappingId={fieldMappingId}
          defaultValue={currentEditorValue}
          onChange={(nextValue) => onChange(shortId, nextValue as FieldMappingEditorValue)}
        />
      </Stack>
    );
  }

  if (attributeType === 'file_multi') {
    // file_multi values are lists of FieldMappingEditorValue dicts (now part of
    // the FieldConfigValue union). Keep only the dict entries; a never-set field
    // falls back to the single editor value wrapped in a list.
    const valueItems = (Array.isArray(value) ? value : []) as Array<
      string | ExtendedFieldMappingEditorValue
    >;
    const storedDicts = valueItems.filter(
      (item): item is ExtendedFieldMappingEditorValue => typeof item === 'object' && item !== null,
    );
    const currentListValue =
      storedDicts.length > 0
        ? storedDicts
        : currentEditorValue !== undefined
          ? [currentEditorValue]
          : undefined;
    return (
      <Stack space={1}>
        <FieldMappingFileListField
          label={title}
          hideLabel
          allowUploadFiles={false}
          variableGroups={fileMultiVariableGroups}
          fieldMappingId={fieldMappingId}
          defaultValue={currentListValue}
          onChange={(nextValue) => onChange(shortId, nextValue)}
        />
      </Stack>
    );
  }

  if (attributeType === 'user') {
    const userOptions = (usersQuery.data ?? []).map((user) => ({
      value: user.shortId,
      label: user.name,
    }));
    // A stored id whose user has since left the company still renders — with
    // an "Unavailable option" placeholder standing in for its label — so it
    // round-trips on save instead of the Select silently showing nothing
    // selected (the same rule the multi-select branch above applies via
    // toSelectedOptions).
    const options =
      currentStringValue && !userOptions.some((option) => option.value === currentStringValue)
        ? [...userOptions, { value: currentStringValue, label: t('unavailableOption') }]
        : userOptions;
    return (
      <Select
        label={title}
        hideLabel
        value={currentStringValue ?? null}
        options={options}
        isDisabled={usersQuery.isLoading}
        onChange={(nextValue) => onChange(shortId, nextValue)}
        placeholder={usersQuery.isLoading ? t('loadingOptions') : t('selectValue')}
      />
    );
  }

  // Every member of JourneyAttributeType has a branch above, so this is
  // unreachable for any type the union knows about. It is not unreachable at
  // runtime: attributeType arrives from the Journeys API, so a type added
  // upstream before it is added here lands on this line and the field renders
  // nothing. A drifted copy of this union missing a type has previously caused
  // the send sidebar to silently render nothing for it. Rendering a wrong
  // input is worse — it would store a value that can never apply — so null is
  // the right fallback, but if a field ever goes mysteriously missing from
  // this panel, look here first.
  return null;
};

const locale = {
  en: {
    enterValue: 'Enter a value...',
    selectValue: 'Select a value...',
    dateFormat: 'DD / MM / YYYY',
    unavailableOption: 'Unavailable option',
    loadingOptions: 'Loading options…',
    fieldMapTitle: 'Field map',
    fieldMapDescription: 'Receive value from a field in the application',
  },
  is: {
    enterValue: 'Sláðu inn gildi…',
    selectValue: 'Veldu gildi…',
    dateFormat: 'DD / MM / ÁÁÁÁ',
    unavailableOption: 'Valkostur ekki í boði',
    loadingOptions: 'Sæki valkosti…',
    fieldMapTitle: 'Gögn úr umsókn',
    fieldMapDescription: 'Nota gildi úr reit í umsókninni',
  },
};
